import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { findBreadcrumbPath } from "../../menu/U2000Menu";
import { U2000AuthContext } from "../../context/auth/U2000AuthContext";
import { useU2000LogicalUnitContext, type BreadcrumbItem } from "../../context/logicalUnit/U2000LogicalUnitContext";
import { Link } from "react-router-dom";
import type { U2000MenuItem } from "../../menu/U2000MenuUtils";

function u2000BreadcrumbSegmentKey(item: BreadcrumbItem | U2000MenuItem, index: number): string | number {
  if ("id" in item && item.id != null && item.id !== "") {
    return String(item.id);
  }
  if (item.href) {
    return item.href;
  }
  if ("link" in item && item.link) {
    return item.link;
  }
  return `crumb-${index}`;
}

export const U2000Breadcrumb: React.FC = () => {
  const context = useContext(U2000AuthContext);
  const { t } = useTranslation();
  const { logicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const auth = context?.auth;

  const breadcrumbPath = useMemo<U2000MenuItem[] | null>(() => {
    if (!auth?.currentMenuItem || !auth?.menu) return null;
    const path = findBreadcrumbPath(auth.menu, auth.currentMenuItem.id);
    return path ?? null;
  }, [auth]);

  const items: BreadcrumbItem[] | U2000MenuItem[] | null = useMemo(() => {
    return logicalUnitBreadcrumb && logicalUnitBreadcrumb.length > 0 ? logicalUnitBreadcrumb : breadcrumbPath;
  }, [logicalUnitBreadcrumb, breadcrumbPath]);

  if (!context) return null;
  if (!items || items.length === 0) return null;

  const Chevron = () => (
    <svg className="mx-2 h-4 w-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <nav aria-label="Breadcrumb" className="my-0 text-sm text-gray-700">
      <ol className="flex flex-wrap items-center gap-y-1 gap-x-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const key = u2000BreadcrumbSegmentKey(item, index);

          const label = (item.label && (t ? t(item.label) : item.label)) || item.label || "";

          const LabelSpan = (
            <span>
              <span className={`inline wrap-break-word ${isLast ? "font-medium text-slate-900" : "italic text-blue-700"}`}>
                {label}
              </span>
            </span>
          );

          return (
            <li key={key} className="flex min-w-0 max-w-full items-center">
              {index > 0 && <Chevron />}
              {item.href && !isLast ? (
                item.href.startsWith("/") ? (
                  <Link
                    to={item.href}
                    className="text-sky-600 hover:text-sky-700 hover:underline underline-offset-2 cursor-pointer transition-colors duration-150 focus:outline-hidden focus:ring-2 focus:ring-sky-200 rounded-sm"
                    aria-label={label}
                  >
                    {LabelSpan}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:text-sky-700 hover:underline underline-offset-2 cursor-pointer transition-colors duration-150 focus:outline-hidden focus:ring-2 focus:ring-sky-200 rounded-sm"
                    aria-label={label}
                  >
                    {LabelSpan}
                  </a>
                )
              ) : (
                <span {...(isLast ? { "aria-current": "page" } : {})}>{LabelSpan}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
